/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { HTMLAttributes, Ref, useState } from 'react';
import { useSelect, UseSelectStateChange } from 'downshift';
import ConfidenceLabel, { ConfidenceLabelProps } from '../ConfidenceLabel';
import styles from './Select.module.css';

export type SelectProps<T> = ConfidenceLabelProps &
  HTMLAttributes<HTMLDivElement> & {
    selectedItem?: T;
    handleSelectedItemChange?: (changes: UseSelectStateChange<T>) => void;
    options: Array<T>;
    itemDisplayFormat?: (item: T | undefined) => string;
    innerRef?: Ref<HTMLButtonElement>;
    innerTabIndex?: number;
    ref?: Ref<HTMLDivElement>;
  };

const defaultItemDisplayFormat = <T,>(item: T | undefined) => {
  if (typeof item === 'string') {
    return item;
  }

  if (typeof item === 'object') {
    const coercedItem = (item as unknown) as any;
    if (coercedItem.hasOwnProperty('name')) {
      return coercedItem.name! // eslint-disable-line
    }
    if (coercedItem.hasOwnProperty('display')) {
      return coercedItem.display! // eslint-disable-line
    }
    return JSON.stringify(coercedItem);
  }
  return '';
};

const Select = <T,>({
  selectedItem,
  handleSelectedItemChange,
  options,
  itemDisplayFormat = defaultItemDisplayFormat,
  confidenceAutomated,
  confidenceLevel,
  confidenceValue,
  ignored,
  className,
  innerRef,
  ref,
  id,
  placeholder,
  innerTabIndex,
  ...divProps
}: SelectProps<T>): JSX.Element => {
  const { isOpen, getToggleButtonProps, getMenuProps, highlightedIndex, getItemProps } = useSelect<T>({
    items: options,
    selectedItem: selectedItem || null,
    onSelectedItemChange: handleSelectedItemChange,
  });

  const automatedClassName = confidenceAutomated && !isOpen ? 'automated' : '';
  const ignoredClassName = ignored ? 'ignored' : '';
  const classNames = `form-control ${styles['cursor']} ${styles['button']} ${automatedClassName} ${ignoredClassName} ${
    selectedItem ? '' : styles['placeholder']
  }`;

  const focusInput = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    ((event.target as HTMLSpanElement).previousSibling as HTMLElement).focus();
  };

  const [isFocused, setIsFocused] = useState(false);

  const toggleButtonProps = getToggleButtonProps({
    placeholder,
    ref: innerRef,
    id,
    disabled: ignored,
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
  });

  const containerClasses = `${styles.container} ${className ? className : ''}`;

  return (
    <div {...divProps} className={containerClasses} ref={ref}>
      <button type="button" {...toggleButtonProps} className={classNames} tabIndex={innerTabIndex || 0}>
        {selectedItem ? itemDisplayFormat(selectedItem) : placeholder}
        {confidenceAutomated && !isOpen && !isFocused && (
          <span className={`${styles['badge-automated']} ${styles['badge-label']}`} onClick={focusInput}>
            Automated
          </span>
        )}
        {ignored && <span className={`${styles['badge-ignored']} ${styles['badge-label']}`}>NOT IN DOCUMENT</span>}
        <div className={`${styles['icon-group']} ${automatedClassName} ${ignoredClassName}`}>
          <span className={`fe fe-chevron-${isOpen ? 'up' : 'down'}`}></span>
          {!isFocused && !isOpen && (
            <ConfidenceLabel
              confidenceAutomated={confidenceAutomated}
              confidenceLevel={confidenceLevel}
              confidenceValue={confidenceValue}
            />
          )}
        </div>
      </button>
      <ul {...getMenuProps()} className={`${styles.menu} ${isOpen ? 'open' : ''}`}>
        {isOpen &&
          options.map((item, index) => (
            <li
              key={`${itemDisplayFormat(item)}${index}`}
              className={highlightedIndex === index ? styles['highlighted'] : undefined}
              {...getItemProps({ item, index })}
            >
              {itemDisplayFormat(item)}
            </li>
          ))}
      </ul>
    </div>
  );
};

export default Select;
